// apps/office/src/pages/office/PriceList.jsx

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Search, ImageIcon, RefreshCw, Tag, Copy,
  Check, X, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import pb from '../../lib/pocketbaseClient';
import { useNavigate } from 'react-router-dom';

const STARTING_ID_NUMBER = 9101;

const PLOT_SIZE_OPTIONS = [
  'Up to 2000 sqft',
  '2001 to 3000 sqft',
  '3001 to 4000 sqft',
  '4001 to 5000 sqft',
];

const BUILDING_TYPES = [
  'single storied',
  'Two storied (G+1)',
  'Three Storied (G+2)',
  'Four Storied (G+3)',
  'Five storied (G+4)',
];

const MAIN_CATEGORIES = ['Design Package', 'General', 'Approval Drawing & Estimate'];

const PACKAGE_OPTIONS = {
  'Design Package': ['Standard Package', 'Advance Package', 'Premium Package'],
  'General': [
    'Floor Plan (Vastu)', '3D exterior elevation', 'structure design',
    'electrical layout', 'plumbing layout', 'working cost estimate',
    '3D floor plan', 'A4 Drawing Sheet',
  ],
  'Approval Drawing & Estimate': ['panchayet approval', 'Bank loan estimate'],
};

// ─── ID helpers ────────────────────────────────────
const generateNextItemId = (existingItems) => {
  if (!existingItems?.length) return `P${STARTING_ID_NUMBER}`;
  const numbers = existingItems
    .map((item) => {
      const match = item.itemId?.match(/^P(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => !isNaN(n) && n > 0);
  if (numbers.length === 0) return `P${STARTING_ID_NUMBER}`;
  const maxNum = Math.max(...numbers);
  return `P${Math.max(maxNum + 1, STARTING_ID_NUMBER)}`;
};

const generateNextItemIdFromSet = (existingIds) => {
  if (!existingIds?.length) return `P${STARTING_ID_NUMBER}`;
  const numbers = existingIds
    .map((id) => {
      const match = id?.match(/^P(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => !isNaN(n) && n > 0);
  if (numbers.length === 0) return `P${STARTING_ID_NUMBER}`;
  const maxNum = Math.max(...numbers);
  return `P${Math.max(maxNum + 1, STARTING_ID_NUMBER)}`;
};

const PriceList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState(null); // if set → update mode
  const [isDuplicateMode, setIsDuplicateMode] = useState(false); // if true → create mode with prefilled data
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    itemId: '', name: '', plotSize: '', buildingType: '', mainCategory: '',
    packageType: '', mrp: '', discountPercent: '', sellingPrice: '', details: '', unit: '',
  });
  const [generatingIds, setGeneratingIds] = useState(false);

  // ─── Selection state ────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ─── Bulk edit state ────────────────────────────────
  const [bulkPlotSize, setBulkPlotSize] = useState('');
  const [bulkBuildingType, setBulkBuildingType] = useState('');
  const [bulkMainCategory, setBulkMainCategory] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const isAuthenticated = pb.authStore.isValid;

  const { data: items = [], isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['priceList'],
    queryFn: () => pb.collection('price_list_items').getFullList({ sort: 'itemId' }),
    enabled: isAuthenticated,
    retry: (failureCount, error) => {
      if (error?.status === 404) return false;
      return failureCount < 1;
    },
  });

  useEffect(() => {
    if (isDialogOpen && !editingItem && !isDuplicateMode) {
      const nextId = generateNextItemId(items);
      setFormData((prev) => ({ ...prev, itemId: nextId }));
    }
  }, [isDialogOpen, editingItem, isDuplicateMode, items]);

  useEffect(() => {
    const mrp = parseFloat(formData.mrp) || 0;
    const discount = parseFloat(formData.discountPercent) || 0;
    const selling = mrp * (1 - discount / 100);
    setFormData((prev) => ({ ...prev, sellingPrice: selling.toFixed(2) }));
  }, [formData.mrp, formData.discountPercent]);

  const validateForm = () => {
    if (!formData.plotSize) { toast.error('Plot Size is required'); return false; }
    if (!formData.buildingType) { toast.error('Building Type is required'); return false; }
    if (!formData.mainCategory) { toast.error('Main Category is required'); return false; }
    if (!formData.packageType) { toast.error('Package Type is required'); return false; }
    if (!formData.mrp || parseFloat(formData.mrp) <= 0) { toast.error('MRP must be positive'); return false; }
    if (!formData.discountPercent || parseFloat(formData.discountPercent) < 0) { toast.error('Discount must be non-negative'); return false; }
    return true;
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const fd = new FormData();
      Object.keys(data).forEach((k) => { if (data[k] != null && k !== 'image') fd.append(k, data[k]); });
      if (imageFile) fd.append('image', imageFile);
      return pb.collection('price_list_items').create(fd);
    },
    onSuccess: () => { queryClient.invalidateQueries(['priceList']); toast.success('✅ Item created!'); resetForm(); refetch(); },
    onError: (err) => toast.error('Create failed: ' + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      if (!id) throw new Error('Missing record ID');
      const fd = new FormData();
      Object.keys(data).forEach((k) => { if (data[k] != null && k !== 'image') fd.append(k, data[k]); });
      if (imageFile) fd.append('image', imageFile);
      return pb.collection('price_list_items').update(id, fd);
    },
    onSuccess: () => { queryClient.invalidateQueries(['priceList']); toast.success('✅ Updated!'); resetForm(); refetch(); },
    onError: (err) => toast.error('Update failed: ' + err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('price_list_items').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['priceList']);
      toast.success('🗑️ Deleted');
      // Remove from selection
      setSelectedIds((prev) => {
        const next = new Set(prev);
        return next;
      });
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  // ─── Bulk update plot/building/category ─────────────
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, updates }) => {
      const results = await Promise.all(
        ids.map((id) => pb.collection('price_list_items').update(id, updates))
      );
      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['priceList']);
      toast.success(`✅ Updated ${variables.ids.length} item(s)`);
      setSelectedIds(new Set());
      setBulkPlotSize('');
      setBulkBuildingType('');
      setBulkMainCategory('');
      setBulkUpdating(false);
      refetch();
    },
    onError: (err) => {
      toast.error('Bulk update failed: ' + err.message);
      setBulkUpdating(false);
    },
  });

  const handleBulkApply = () => {
    const updates = {};
    if (bulkPlotSize) updates.plotSize = bulkPlotSize;
    if (bulkBuildingType) updates.buildingType = bulkBuildingType;
    if (bulkMainCategory) updates.mainCategory = bulkMainCategory;

    if (Object.keys(updates).length === 0) {
      toast.error('Select at least one field to update');
      return;
    }
    if (selectedIds.size === 0) {
      toast.error('Select items first');
      return;
    }
    setBulkUpdating(true);
    bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), updates });
  };

  // ─── Selection helpers ──────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleItems) => {
    if (visibleItems.length > 0 && visibleItems.every((i) => selectedIds.has(i.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleItems.map((i) => i.id)));
    }
  };

  const generateMissingIds = async () => {
    await refetch();
    const currentItems = queryClient.getQueryData(['priceList']) || [];
    const missing = currentItems.filter((item) => !item.itemId || item.itemId === '');
    if (missing.length === 0) { toast.info('All items have IDs.'); return; }
    setGeneratingIds(true);
    const existingIds = currentItems.filter((i) => i.itemId && i.itemId !== '').map((i) => i.itemId);
    let success = 0;
    for (const item of missing) {
      try {
        const nextId = generateNextItemIdFromSet(existingIds);
        await pb.collection('price_list_items').update(item.id, { itemId: nextId });
        existingIds.push(nextId);
        success++;
      } catch (err) { console.error(err); }
    }
    setGeneratingIds(false);
    queryClient.invalidateQueries(['priceList']);
    toast.success(`✨ Generated ${success} IDs`);
    refetch();
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
    setIsDuplicateMode(false);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      itemId: '', name: '', plotSize: '', buildingType: '', mainCategory: '',
      packageType: '', mrp: '', discountPercent: '', sellingPrice: '', details: '', unit: '',
    });
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setIsDuplicateMode(false);
    const nextId = generateNextItemId(items);
    setFormData({
      itemId: nextId, name: '', plotSize: '', buildingType: '', mainCategory: '',
      packageType: '', mrp: '', discountPercent: '', sellingPrice: '', details: '', unit: '',
    });
    setImageFile(null);
    setImagePreview('');
    setIsDialogOpen(true);
  };

  const openEditModal = (item) => {
    if (!item?.id) { toast.error('Invalid item'); return; }
    setEditingItem(item);
    setIsDuplicateMode(false);
    setFormData({
      itemId: item.itemId || '',
      name: item.name || '',
      plotSize: item.plotSize || '',
      buildingType: item.buildingType || '',
      mainCategory: item.mainCategory || '',
      packageType: item.packageType || '',
      mrp: item.mrp?.toString() || '',
      discountPercent: item.discountPercent?.toString() || '',
      sellingPrice: item.sellingPrice?.toString() || '',
      details: item.details || '',
      unit: item.unit || '',
    });
    setImagePreview(item.image ? pb.files.getURL(item, item.image) : '');
    setImageFile(null);
    setIsDialogOpen(true);
  };

  const openDuplicateModal = (item) => {
    if (!item?.id) { toast.error('Invalid item'); return; }
    setEditingItem(null);
    setIsDuplicateMode(true);
    const nextId = generateNextItemId(items);
    setFormData({
      itemId: nextId,                       // fresh ID
      name: item.name || '',
      plotSize: item.plotSize || '',
      buildingType: item.buildingType || '',
      mainCategory: item.mainCategory || '',
      packageType: item.packageType || '',
      mrp: item.mrp?.toString() || '',
      discountPercent: item.discountPercent?.toString() || '',
      sellingPrice: item.sellingPrice?.toString() || '',
      details: item.details || '',
      unit: item.unit || '',
    });
    setImagePreview(item.image ? pb.files.getURL(item, item.image) : '');
    setImageFile(null);
    setIsDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const data = {
      itemId: formData.itemId,
      name: formData.name || `${formData.packageType} - ${formData.buildingType}`,
      plotSize: formData.plotSize,
      buildingType: formData.buildingType,
      mainCategory: formData.mainCategory,
      packageType: formData.packageType,
      mrp: parseFloat(formData.mrp),
      discountPercent: parseFloat(formData.discountPercent),
      sellingPrice: parseFloat(formData.sellingPrice) || 0,
      unit: formData.unit || 'package',
      details: formData.details || '',
    };
    if (editingItem && !isDuplicateMode) {
      if (!editingItem.id) { toast.error('Missing ID'); return; }
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      // Create new (both fresh + duplicate)
      createMutation.mutate(data);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

  const filteredItems = items.filter((i) =>
    i.itemId?.toLowerCase().includes(search.toLowerCase()) ||
    i.name?.toLowerCase().includes(search.toLowerCase()) ||
    i.packageType?.toLowerCase().includes(search.toLowerCase()) ||
    i.plotSize?.toLowerCase().includes(search.toLowerCase())
  );

  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every((i) => selectedIds.has(i.id));

  if (!isAuthenticated) return (
    <div className="text-center py-20">
      <h2 className="text-2xl font-bold text-red-500">Please log in</h2>
      <Button onClick={() => navigate('/office/login')} className="mt-4">Login</Button>
    </div>
  );
  if (queryError) return (
    <div className="text-center py-10">
      <p className="text-red-500">Error loading data.</p>
      <Button onClick={() => refetch()}>Retry</Button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <Tag className="w-8 h-8" /> Price List Management
            </h1>
            <p className="text-white/80">Plot Size → Building → Category → Package</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={generateMissingIds} disabled={generatingIds} className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white">
              <RefreshCw className={`w-4 h-4 mr-2 ${generatingIds ? 'animate-spin' : ''}`} />
              {generatingIds ? 'Generating...' : 'Generate IDs'}
            </Button>
            <Button onClick={openCreateModal} className="bg-white text-orange-600 hover:bg-orange-50 shadow-lg">
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search by ID, name, package, plot size..." className="pl-9 border-orange-200 focus:border-orange-400" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-30 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-2xl p-4 shadow-lg">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear
              </button>
            </div>

            <div className="h-6 w-px bg-orange-300" />

            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Bulk Update:
            </span>

            {/* Bulk Plot Size */}
            <select
              value={bulkPlotSize}
              onChange={(e) => setBulkPlotSize(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-orange-200 bg-white focus:ring-2 focus:ring-orange-400 outline-none"
            >
              <option value="">📏 Plot Size...</option>
              {PLOT_SIZE_OPTIONS.map((ps) => (
                <option key={ps} value={ps}>{ps}</option>
              ))}
            </select>

            {/* Bulk Building Type */}
            <select
              value={bulkBuildingType}
              onChange={(e) => setBulkBuildingType(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-orange-200 bg-white focus:ring-2 focus:ring-orange-400 outline-none"
            >
              <option value="">🏢 Building Type...</option>
              {BUILDING_TYPES.map((bt) => (
                <option key={bt} value={bt}>{bt}</option>
              ))}
            </select>

            {/* Bulk Main Category */}
            <select
              value={bulkMainCategory}
              onChange={(e) => setBulkMainCategory(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-orange-200 bg-white focus:ring-2 focus:ring-orange-400 outline-none"
            >
              <option value="">📚 Category...</option>
              {MAIN_CATEGORIES.map((mc) => (
                <option key={mc} value={mc}>{mc}</option>
              ))}
            </select>

            <Button
              onClick={handleBulkApply}
              disabled={bulkUpdating || (!bulkPlotSize && !bulkBuildingType && !bulkMainCategory)}
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            >
              {bulkUpdating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Applying...</>
              ) : (
                <><Check className="w-4 h-4" /> Apply to All ({selectedIds.size})</>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            💡 Only the fields you select will be updated — others stay unchanged.
          </p>
        </div>
      )}

      {/* Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
              <TableRow>
                <TableHead className="w-10 text-orange-700">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => toggleSelectAll(filteredItems)}
                    className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-orange-500"
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead className="text-orange-700 font-semibold">Image</TableHead>
                <TableHead className="text-orange-700 font-semibold">Item ID</TableHead>
                <TableHead className="text-orange-700 font-semibold">Plot Size</TableHead>
                <TableHead className="text-orange-700 font-semibold">Building</TableHead>
                <TableHead className="text-orange-700 font-semibold">Category</TableHead>
                <TableHead className="text-orange-700 font-semibold">Package</TableHead>
                <TableHead className="text-orange-700 font-semibold">MRP</TableHead>
                <TableHead className="text-orange-700 font-semibold">Disc</TableHead>
                <TableHead className="text-orange-700 font-semibold">Selling</TableHead>
                <TableHead className="text-orange-700 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">No items found</TableCell></TableRow>
              ) : (
                filteredItems.map((item) => {
                  const selected = selectedIds.has(item.id);
                  return (
                    <TableRow
                      key={item.id}
                      className={`transition-colors ${selected ? 'bg-orange-100/70' : 'hover:bg-orange-50/50'}`}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelect(item.id)}
                          className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-orange-500"
                          aria-label={`Select ${item.itemId}`}
                        />
                      </TableCell>
                      <TableCell>
                        {item.image ? (
                          <img src={pb.files.getURL(item, item.image)} alt={item.name} className="w-12 h-12 object-cover rounded-lg border-2 border-orange-200" />
                        ) : (
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-orange-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-orange-600 font-bold">{item.itemId || '—'}</TableCell>
                      <TableCell>
                        <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                          {item.plotSize || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="capitalize text-sm">{item.buildingType}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                          item.mainCategory === 'Design Package' ? 'bg-violet-100 text-violet-700' :
                          item.mainCategory === 'Approval Drawing & Estimate' ? 'bg-fuchsia-100 text-fuchsia-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {item.mainCategory}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs">{item.packageType}</span>
                      </TableCell>
                      <TableCell className="text-slate-500 line-through">{formatPrice(item.mrp)}</TableCell>
                      <TableCell>
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">{item.discountPercent}%</span>
                      </TableCell>
                      <TableCell className="font-bold text-amber-600 text-lg">{formatPrice(item.sellingPrice)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(item)}
                            className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDuplicateModal(item)}
                            className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Duplicate (copy with new ID)"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { if (window.confirm('Delete?')) deleteMutation.mutate(item.id); }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-orange-600 flex items-center gap-2">
              {isDuplicateMode && <Copy className="w-6 h-6" />}
              {editingItem && !isDuplicateMode ? '✏️ Edit Item' : isDuplicateMode ? '📋 Duplicate Item' : '➕ Add New Item'}
            </DialogTitle>
            {isDuplicateMode && (
              <p className="text-sm text-slate-500 mt-1">
                New Item ID <span className="font-mono font-bold text-orange-600">{formData.itemId}</span> auto-assigned. Save creates a new record.
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            {/* Item ID */}
            <div>
              <Label>Item ID (auto)</Label>
              <Input value={formData.itemId} disabled className="bg-orange-50 text-orange-600 font-mono" />
            </div>

            {/* Image */}
            <div>
              <Label>Image</Label>
              <div className="flex items-center gap-4">
                {imagePreview && <img src={imagePreview} className="w-20 h-20 object-cover rounded-lg border-2 border-orange-300" />}
                <Input type="file" accept="image/*" onChange={handleImageChange} />
              </div>
              {isDuplicateMode && !imageFile && (
                <p className="text-xs text-slate-500 mt-1.5">
                  ℹ️ Copy-তে image আপলোড করা যাবে, না করলে image ছাড়া save হবে।
                </p>
              )}
            </div>

            {/* Plot Size */}
            <div>
              <Label>Plot Size * <span className="text-xs text-slate-400 font-normal">(Primary category)</span></Label>
              <Select value={formData.plotSize} onValueChange={(val) => setFormData({ ...formData, plotSize: val })}>
                <SelectTrigger className="border-orange-200"><SelectValue placeholder="Select plot size" /></SelectTrigger>
                <SelectContent>
                  {PLOT_SIZE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Building Type */}
            <div>
              <Label>Building Type *</Label>
              <Select value={formData.buildingType} onValueChange={(val) => setFormData({ ...formData, buildingType: val })}>
                <SelectTrigger className="border-orange-200"><SelectValue placeholder="Select building type" /></SelectTrigger>
                <SelectContent>
                  {BUILDING_TYPES.map((bt) => (
                    <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Main Category */}
            <div>
              <Label>Main Category *</Label>
              <Select value={formData.mainCategory} onValueChange={(val) => setFormData({ ...formData, mainCategory: val, packageType: '' })}>
                <SelectTrigger className="border-orange-200"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Design Package">📦 Design Package</SelectItem>
                  <SelectItem value="General">📋 General</SelectItem>
                  <SelectItem value="Approval Drawing & Estimate">🏛️ Approval Drawing & Estimate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Package Type */}
            {formData.mainCategory && (
              <div>
                <Label>Package Type *</Label>
                <Select value={formData.packageType} onValueChange={(val) => setFormData({ ...formData, packageType: val })}>
                  <SelectTrigger className="border-orange-200"><SelectValue placeholder="Select package type" /></SelectTrigger>
                  <SelectContent>
                    {PACKAGE_OPTIONS[formData.mainCategory]?.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* MRP + Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>MRP (₹) *</Label>
                <Input type="number" step="any" value={formData.mrp} onChange={(e) => setFormData({ ...formData, mrp: e.target.value })} className="border-orange-200" placeholder="50000" />
              </div>
              <div>
                <Label>Discount (%) *</Label>
                <Input type="number" step="any" value={formData.discountPercent} onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })} className="border-orange-200" placeholder="15" />
              </div>
            </div>

            {/* Selling Price */}
            <div>
              <Label>Selling Price (auto)</Label>
              <Input value={formData.sellingPrice} disabled className="bg-orange-50 text-amber-600 font-bold" />
              <p className="text-xs text-slate-500">MRP × (1 – Discount/100)</p>
            </div>

            {/* Unit */}
            <div>
              <Label>Unit</Label>
              <Input value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} className="border-orange-200" placeholder="sq.ft, package" />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} className="border-orange-200" placeholder="What's included..." />
            </div>

            {/* Display Name */}
            <div>
              <Label>Display Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="border-orange-200" placeholder="Auto-generate if empty" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
              {editingItem && !isDuplicateMode ? '💾 Update' : isDuplicateMode ? '📋 Create Duplicate' : '✨ Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceList;
